import React from 'react';
import { Link } from 'react-router-dom';
import PortableTextContent from '../ui/PortableTextContent';
import RichText from '../ui/RichText';
import MemberCard from '../cards/MemberCard';
import '../../css/common/MemberCard.css';
import '../../css/pages/PageBlocks.css';

const isExternal = (url = '') => /^https?:\/\//i.test(url) || url.startsWith('mailto:');

const BlockButton = ({ text, link }) => {
  if (!text || !link) return null;
  if (isExternal(link)) {
    return (
      <a className="page-block-button" href={link} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );
  }
  return (
    <Link className="page-block-button" to={link}>
      {text}
    </Link>
  );
};

const HeroBlock = ({ block }) => {
  const style = block.backgroundImageUrl
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${block.backgroundImageUrl})` }
    : undefined;
  return (
    <section className={`page-block-hero${block.backgroundImageUrl ? ' page-block-hero--image' : ''}`} style={style}>
      <div className="page-block-hero-inner">
        <h2 className="page-block-hero-heading">{block.heading}</h2>
        {block.subheading && <p className="page-block-hero-subheading">{block.subheading}</p>}
        <BlockButton text={block.buttonText} link={block.buttonLink} />
      </div>
    </section>
  );
};

const RichTextBlock = ({ block }) => (
  <section className="page-block-text">
    {block.heading && <h2 className="page-block-heading">{block.heading}</h2>}
    <PortableTextContent value={block.content} />
  </section>
);

const CardGridBlock = ({ block }) => {
  const cards = block.cards || [];
  if (cards.length === 0) return null;
  return (
    <section className="page-block-section">
      {block.heading && <h2 className="page-block-heading">{block.heading}</h2>}
      <div className="page-block-card-grid">
        {cards.map((card, i) => (
          <article className="page-block-card" key={i}>
            {card.imageUrl && (
              <div className="page-block-card-image">
                <img src={card.imageUrl} alt={card.title || ''} loading="lazy" />
              </div>
            )}
            <div className="page-block-card-body">
              {card.title && <h3 className="page-block-card-title">{card.title}</h3>}
              {card.text && <RichText value={card.text} className="page-block-card-text" />}
              {card.linkText && card.linkUrl && (
                <BlockButton text={card.linkText} link={card.linkUrl} />
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

const MemberGridBlock = ({ block }) => {
  const members = block.members || [];
  if (members.length === 0) return null;
  return (
    <section className="page-block-section member-page-section">
      {block.heading && <h2 className="page-block-heading">{block.heading}</h2>}
      <div className="member-page-grid">
        {members.map((member, i) => (
          <div className="member-page-card-wrapper" key={i} title={member.bio || ''}>
            <MemberCard
              name={member.name}
              title={member.role}
              imageUrl={member.imageUrl}
              index={i}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

const ImageBlock = ({ block }) => {
  const images = (block.images || []).filter((img) => img && img.url);
  if (images.length === 0) return null;
  return (
    <section className="page-block-section">
      <div className={`page-block-gallery page-block-gallery--${Math.min(images.length, 3)}`}>
        {images.map((img, i) => (
          <img key={i} src={img.url} alt={block.caption || ''} loading="lazy" />
        ))}
      </div>
      {block.caption && <p className="page-block-caption">{block.caption}</p>}
    </section>
  );
};

const CtaBlock = ({ block }) => (
  <section className="page-block-cta">
    <h2 className="page-block-cta-heading">{block.heading}</h2>
    {block.text && <RichText value={block.text} className="page-block-cta-text" />}
    <BlockButton text={block.buttonText} link={block.buttonLink} />
  </section>
);

const RENDERERS = {
  heroBlock: HeroBlock,
  richTextBlock: RichTextBlock,
  cardGridBlock: CardGridBlock,
  memberGridBlock: MemberGridBlock,
  imageBlock: ImageBlock,
  ctaBlock: CtaBlock,
};

const PageBlocks = ({ sections = [] }) => (
  <div className="page-blocks">
    {sections.map((block, i) => {
      const Renderer = RENDERERS[block._type];
      if (!Renderer) return null;
      return <Renderer key={block._key || i} block={block} />;
    })}
  </div>
);

export default PageBlocks;
